// components/chakra/NavbarWithCallToAction/NavbarWithCallToAction.tsx

import {
  Box,
  Container,
  Flex,
  HStack,
  IconButton,
  Link,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { OneMark, WatermarkO, GoldRibbon, BridgePhoto, ribbonAccentFor, ONE_NAVY, ONE_NAVY_2, ONE_GOLD } from "../brand/OneGroupBrand";

import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { NotificationBell } from "../../NotificationBell";
import { brandConfig } from "../../../brandConfig";
import { SecureMembershipButton, LoginWithSmsModal } from "../../../pages/authentication";
import { FiLogOut } from 'react-icons/fi';

const VERTICAL_SITE_NAMES: Record<string, string> = {
  "/property": "One Property Australia",
  "/finance": "One Finance Australia",
  "/solar": "One Solar Australia",
  "/water": "One Water Australia",
};

export const NavbarWithCallToAction = () => {
  const { isOpen: isLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();

  const siteName = Object.entries(VERTICAL_SITE_NAMES).find(([prefix]) =>
    location.pathname.startsWith(prefix)
  )?.[1];

  const handleLoginSuccess = () => {
    onLoginClose();
    window.location.reload();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <Box
        position="sticky"
        top={0}
        left={0}
        right={0}
        zIndex="sticky"
        bg={ONE_NAVY}
        bgGradient={`linear(140deg, ${ONE_NAVY} 0%, ${ONE_NAVY_2} 100%)`}
        color="white"
        boxShadow="0 2px 10px rgba(7, 30, 62, 0.25)"
        margin={0}
        padding={0}
        width="100%"
        overflow="hidden"
        data-testid="site-header"
      >
        {/* Monica, 13 Oct 2026: her Story Bridge photo under a navy veil, the ribbon, the watermark O. */}
        <BridgePhoto veil="header" />
        <WatermarkO right={{ base: "-14px", md: "6%" }} top={{ base: "-6px", md: "-22px" }} fontSize={{ base: "110px", md: "170px" }} />
        <Container maxW="7xl" position="relative" zIndex={1} px={{ base: 3, md: 6 }}>
          <Flex
            minH={{ base: "56px", md: "64px" }}
            py={{ base: 2, md: 4 }}
            align="center"
            justify="space-between"
            flexWrap={{ base: "wrap", md: "nowrap" }}
            gap={{ base: 2, md: 4 }}
            width="100%"
          >
            <HStack spacing={{ base: 2, md: 6 }}>
              <Link
                as={RouterLink}
                to="/"
                aria-label={`${siteName ?? brandConfig.siteName} home`}
                flexShrink={0}
              >
                <OneMark siteName={siteName ?? "One Group Australia"} />
              </Link>

              {/* Services Dropdown */}
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  variant="ghost"
                  size={{ base: "sm", md: "md" }}
                  fontWeight="medium"
                  color="white"
                  minH="44px"
                  _hover={{ bg: "whiteAlpha.200" }}
                  _active={{ bg: "whiteAlpha.300" }}
                >
                  Services
                </MenuButton>
                <MenuList
                  bg="#FFFFFF"
                  borderColor="rgba(20, 28, 73, 0.12)"
                  boxShadow="0 4px 16px rgba(20, 28, 73, 0.12)"
                >
                  <MenuItem
                    as={RouterLink}
                    to="/water"
                    bg="#FFFFFF"
                    color="#141C49"
                    _hover={{ bg: "#F5F5F7", color: "#6FAFD6" }}
                  >
                    One Water Australia
                  </MenuItem>
                  <MenuItem
                    as={RouterLink}
                    to="/solar"
                    bg="#FFFFFF"
                    color="#141C49"
                    _hover={{ bg: "#F5F5F7", color: "#C69214" }}
                  >
                    One Solar Australia
                  </MenuItem>
                  <MenuItem
                    as={RouterLink}
                    to="/finance"
                    bg="#FFFFFF"
                    color="#141C49"
                    _hover={{ bg: "#F5F5F7", color: "#2F3A4A" }}
                  >
                    One Finance Australia
                  </MenuItem>
                  <MenuItem
                    as={RouterLink}
                    to="/property"
                    bg="#FFFFFF"
                    color="#141C49"
                    _hover={{ bg: "#F5F5F7", color: "#0D7F6E" }}
                  >
                    One Property Australia
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>

          <HStack spacing={{ base: 1, md: 4 }} flexShrink={0}>
            {/* Jessica, 9 Oct 2026: "I like the Track my Build … button in the top corner". Clients sign in and land on their build. */}
            <Button
              as={RouterLink}
              to="/my-build"
              variant="outline"
              borderColor={ONE_GOLD}
              color="white"
              size={{ base: "sm", md: "md" }}
              minH="44px"
              px={{ base: 2, md: 4 }}
              fontWeight="medium"
              title="Building with One Group? Sign in to see your build: progress, photos and your team."
              data-testid="nav-track-my-build"
              _hover={{ bg: "whiteAlpha.200" }}
            >
              <Box as="span" display={{ base: "none", md: "inline" }}>Track my build</Box>
              <Box as="span" display={{ base: "inline", md: "none" }}>My build</Box>
            </Button>
            <Button
              as={RouterLink}
              to="/contact"
              bg={ONE_GOLD}
              color={ONE_NAVY}
              size={{ base: "sm", md: "md" }}
              minH="44px"
              _hover={{ bg: "#d4b383" }}
              fontWeight="semibold"
              title="Tell us about your plans and we'll get back to you with a quote."
            >
              Get Quote
            </Button>
            {isAuthenticated && <NotificationBell />}
            {isAuthenticated ? (
              <IconButton
                aria-label="Logout"
                icon={<FiLogOut />}
                size="sm"
                variant="ghost"
                color="red.200"
                minH="44px"
                minW="44px"
                title="Sign out"
                onClick={handleLogout}
                _hover={{
                  color: "red.100",
                  bg: "whiteAlpha.200",
                }}
              />
            ) : (
              <SecureMembershipButton />
            )}
          </HStack>
        </Flex>
      </Container>
      <GoldRibbon accent={ribbonAccentFor(location.pathname)} position="relative" zIndex={1} />

      {/* SMS Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </Box>
  );
};